import { CommonModule } from '@angular/common';
import { Component, ElementRef, inject, Input, OnInit, ViewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { format, isValid, parseISO } from 'date-fns';
import { PhotosVideosViewerItem } from '../../../models/photos-videos-viewer.model';
import { AvatarComponent } from '../../../../../_shared/components/avatar/avatar.component';
import { TranslocoPipe , TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-photos-videos-viewer-modal',
  standalone: true,
  imports: [CommonModule, AvatarComponent, TranslocoPipe],
  templateUrl: './photos-videos-viewer-modal.component.html',
  styleUrl: './photos-videos-viewer-modal.component.scss'
})
export class PhotosVideosViewerModalComponent implements OnInit {
  private readonly transloco = inject(TranslocoService);
  private activeModal = inject(NgbActiveModal);
  @ViewChild('videoPlayer') private videoPlayer?: ElementRef<HTMLVideoElement>;

  @Input() title: string = '';
  @Input() contextLabel?: string;
  @Input() reportedBy?: string | null;
  @Input() reportedAt?: string | Date | null;
  @Input() reportedByProfilePictureUrl?: string | null;
  @Input() items: PhotosVideosViewerItem[] = [];
  @Input() downloadLinkResolver?: (item: PhotosVideosViewerItem) => Promise<string | null>;

  currentIndex: number = 0;
  isDownloading: boolean = false;
  isMediaLoading: boolean = true;
  loadedThumbnailKeys = new Set<string>();

  ngOnInit(): void {
    this.items = this.items ?? [];
    this.isMediaLoading = this.items.length > 0;
  }

  get currentItem(): PhotosVideosViewerItem | null {
    return this.items[this.currentIndex] ?? null;
  }

  get uploaderName(): string {
    return this.currentItem?.uploadedBy?.trim() || this.transloco.translate('common.unknown');
  }

  get uploadedAtLabel(): string {
    const uploadedAt = this.currentItem?.uploadedAt;
    if (!uploadedAt) {
      return '';
    }

    return this.formatDateLabel(uploadedAt);
  }

  get hasUploaderDetails(): boolean {
    return !!(this.currentItem?.uploadedBy || this.currentItem?.uploadedAt);
  }

  get reportedAtLabel(): string {
    return this.reportedAt ? this.formatDateLabel(this.reportedAt) : '';
  }

  get hasReporterDetails(): boolean {
    return !!(this.reportedBy || this.reportedAt);
  }

  selectItem(index: number): void {
    if (index >= 0 && index < this.items.length) {
      this.currentIndex = index;
      this.isMediaLoading = this.isImage(this.currentItem) || this.isVideo(this.currentItem);
    }
  }

  previous(): void {
    this.currentIndex =
      this.currentIndex === 0 ? this.items.length - 1 : this.currentIndex - 1;
    this.isMediaLoading = this.isImage(this.currentItem) || this.isVideo(this.currentItem);
  }

  next(): void {
    this.currentIndex =
      this.currentIndex === this.items.length - 1 ? 0 : this.currentIndex + 1;
    this.isMediaLoading = this.isImage(this.currentItem) || this.isVideo(this.currentItem);
  }

  markMediaLoading(): void {
    this.isMediaLoading = true;
  }

  markMediaReady(): void {
    this.isMediaLoading = false;
  }

  markThumbnailReady(item: PhotosVideosViewerItem): void {
    this.loadedThumbnailKeys.add(this.getItemKey(item));
  }

  isThumbnailLoading(item: PhotosVideosViewerItem): boolean {
    return this.isImage(item) && !this.loadedThumbnailKeys.has(this.getItemKey(item));
  }

  isImage(item: PhotosVideosViewerItem | null): boolean {
    if (!item) {
      return false;
    }

    const mediaType = item.mediaType?.toLowerCase() ?? '';
    const extension = this.normalizedExtension(item);
    return mediaType.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(extension);
  }

  isVideo(item: PhotosVideosViewerItem | null): boolean {
    if (!item) {
      return false;
    }

    const mediaType = item.mediaType?.toLowerCase() ?? '';
    const extension = this.normalizedExtension(item);
    return mediaType.startsWith('video/') || ['mp4', 'mov', 'webm', 'm4v', 'avi'].includes(extension);
  }

  getItemKey(item: PhotosVideosViewerItem): string {
    return item.reference || item.url || item.name;
  }

  private normalizedExtension(item: PhotosVideosViewerItem): string {
    return (item.extension ?? '').replace('.', '').toLowerCase();
  }

  private buildFileName(item: PhotosVideosViewerItem): string {
    const base = item.name?.trim() || item.reference;
    const extension = item.extension?.trim();

    if (!extension) {
      return base;
    }

    return extension.startsWith('.') ? `${base}${extension}` : `${base}.${extension}`;
  }

  async downloadItem(item: PhotosVideosViewerItem | null): Promise<void> {
    if (this.isDownloading) {
      return;
    }

    this.isDownloading = true;
    try {
      await this.saveItem(item);
    } finally {
      this.isDownloading = false;
    }
  }

  async downloadAll(): Promise<void> {
    if (this.items.length === 0 || this.isDownloading) {
      return;
    }

    this.isDownloading = true;
    try {
      for (const item of this.items) {
        await this.saveItem(item);
        await this.delay(400);
      }
    } finally {
      this.isDownloading = false;
    }
  }

  get isFullscreen(): boolean {
    return !!document.fullscreenElement;
  }

  async toggleFullscreen(element: HTMLElement): Promise<void> {
    if (document.fullscreenElement) {
      await document.exitFullscreen();
      this.syncVideoLayout();
      return;
    }

    if (element.requestFullscreen) {
      await element.requestFullscreen();
      this.syncVideoLayout();
    }
  }

  private syncVideoLayout(): void {
    requestAnimationFrame(() => {
      const video = this.videoPlayer?.nativeElement;
      if (!video) return;

      video.style.display = 'none';
      void video.offsetHeight;
      video.style.display = '';
      window.dispatchEvent(new Event('resize'));
    });
  }

  private async saveItem(item: PhotosVideosViewerItem | null): Promise<void> {
    if (!item) {
      return;
    }

    let downloadUrl: string | null = item.url ?? null;
    if (this.downloadLinkResolver) {
      const resolved = await this.downloadLinkResolver(item);
      if (resolved) {
        downloadUrl = resolved;
      }
    }

    if (!downloadUrl) {
      return;
    }

    this.triggerAnchorDownload(downloadUrl, this.buildFileName(item));
  }

  private triggerAnchorDownload(url: string, fileName: string): void {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private formatDateLabel(value: string | Date): string {
    const date = value instanceof Date ? value : parseISO(value);
    return isValid(date) ? format(date, 'dd MMM yy HH:mm') : '';
  }

  close(): void {
    this.activeModal.dismiss();
  }
}
